/**
 * The id of the studio a row belongs to, whether the API populated it
 * (`{ _id, studioName }`) or sent the bare id.
 *
 * Row actions pin their write to this (apiService.atStudio): under "All
 * locations" the switcher sends `__all__`, which the server refuses for writes.
 */
export function studioIdOf(studio: any): string | null {
  if (!studio) return null;
  if (typeof studio === 'string') return studio;
  return studio._id ? String(studio._id) : null;
}

/**
 * The location an instructor / staff row is acted on at: the one being viewed
 * when there is one, else the first location they work at (`studios[]`), else
 * the legacy single `studio`.
 */
export function homeStudioOf(row: any, viewing?: string | null): string | null {
  if (viewing) return viewing;
  return studioIdOf(row?.studios?.[0]) || studioIdOf(row?.studio);
}

/** A booking's location: its own `studio`, else its session's or event's. */
export function bookingStudioOf(booking: any): string | null {
  return (
    studioIdOf(booking?.studio) ||
    studioIdOf(booking?.session?.studio) ||
    studioIdOf(booking?.event?.studio)
  );
}

const MANAGER_OR_ABOVE = ['manager', 'studio_owner', 'brand_owner'];

/**
 * Where to pin a write on an instructor / staff row (`studios[]`, legacy
 * `studio`).
 *
 * Only under "All locations" — anywhere else this returns undefined and the
 * switcher / token decides, exactly as before. Under All it prefers a location
 * that is BOTH on the row and one the caller holds, ideally one where the
 * caller is at least a manager; failing that, the first location the caller
 * holds on the row, then the row's first location.
 */
export function actingStudioOf(
  row: any,
  opts: {
    isAllLocations: boolean;
    myLocationIds?: string[];
    roleByStudio?: Record<string, string> | null;
  },
): string | undefined {
  if (!opts.isAllLocations) return undefined;
  const rowIds = [
    ...((Array.isArray(row?.studios) ? row.studios : []) as any[]).map(studioIdOf),
    studioIdOf(row?.studio),
  ].filter((id): id is string => !!id);
  const mine = opts.myLocationIds ?? [];
  const shared = mine.length ? rowIds.filter((id) => mine.includes(id)) : rowIds;
  const rbs = opts.roleByStudio;
  const strong = rbs ? shared.find((id) => MANAGER_OR_ABOVE.includes(rbs[id])) : undefined;
  return strong || shared[0] || rowIds[0] || undefined;
}

/**
 * Split the two list responses into Active and Deactivated, one card per
 * person. Robust to a backend that does not know `status` yet (it returns the
 * same full list twice): sorted by the record's own state and de-duplicated.
 */
export function splitInstructorLists(activeRes: any[], removedRes: any[]) {
  const active = (activeRes ?? []).filter((i: any) => !i?.isDisabled);
  const activeIds = new Set(active.map((i: any) => String(i._id)));
  const seen = new Set<string>();
  const deactivated = [...(activeRes ?? []).filter((i: any) => i?.isDisabled), ...(removedRes ?? [])].filter((i: any) => {
    const id = String(i?._id);
    if (activeIds.has(id) || seen.has(id)) return false;
    seen.add(id);
    return true;
  });
  return { active, deactivated };
}
